import * as React from "react"
import {
  LayoutAnimation,
  Linking,
  PixelRatio,
  Platform,
  Pressable,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native"

import { FontAwesome } from "@expo/vector-icons"
import * as Clipboard from "expo-clipboard"
import * as Haptics from "expo-haptics"
import { getLocales } from "expo-localization"
import { useRouter } from "expo-router"
import { CountryCode, findPhoneNumbersInText } from "libphonenumber-js"
import LinkifyIt from "linkify-it"
import { observer } from "mobx-react-lite"
import Markdown, { RenderRules } from "react-native-markdown-display"
import { useSafeAreaFrame } from "react-native-safe-area-context"
import { FeedbackRating } from "types/feedback"
import { TMessage } from "types/transcript"

import { Text } from "src/components/Ignite/Text"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useCurrentConversation, usePastConversations } from "src/queries/useConversation"
import { ThemedStyle } from "src/theme"
import { getMediaTypeFromEvent, MediaType } from "src/utils/mediaTypes"
import { openWebsite } from "src/utils/openWebsite"
import { formatTimeAgo, timeAgo } from "src/utils/time"
import { MediaThumbnail } from "../MediaPanel/MediaThumbnail"
import { WebsiteThumbnail } from "../MediaPanel/WebThumbnail"
import { SystemMessage } from "./SystemMessage"

interface MessageBubbleProps {
  // FIXME: typing and thinking indicators don't map to specific complete API messages, but that's what a TMessage type represents.
  // A fake TMessage is created for ThinkingIndicator and TypingIndicator to they may render as a MessageBubble.
  // Really we should have a separate set of props for a MessageBubble and a full Message or either indicator
  // should be able to provide the needed props to render one.
  item: TMessage
  typingIndicator?: boolean
  thinkingIndicator?: boolean
  overrideText?: string
  style?: ViewStyle
  onFeedbackPress?: (message: TMessage, rating?: FeedbackRating) => void | undefined
  onExpand?: (message: TMessage) => void
  expanded?: boolean
  children?: React.ReactNode
  containerWidth?: number
}

// Initialize linkify instance
const linkify = LinkifyIt()

/**
 * UI Component for message item, in message list (FlatList).
 */
export const MessageBubble = observer(function MessageBubble(props: MessageBubbleProps) {
  const {
    item,
    style,
    overrideText,
    onFeedbackPress,
    children,
    expanded,
    onExpand,
    containerWidth,
  } = props
  const {
    themed,
    theme: { colors, typography, spacing },
  } = useAppTheme()
  const { width } = useSafeAreaFrame()
  const { fontScale } = useWindowDimensions()
  const { apiHost } = useAuth()
  const router = useRouter()

  const { data: currentConversation } = useCurrentConversation()
  const { pastConversations } = usePastConversations()

  // Resolve the conversation's active_task_id so MediaThumbnail can refresh
  // stale signed thumbnail URLs even when the event itself has no task_id.
  const conversationTaskId = React.useMemo(() => {
    if (!item.conversation_id) return undefined
    if (currentConversation?.id === item.conversation_id) return currentConversation.active_task_id
    return pastConversations.find((c) => c?.id === item.conversation_id)?.active_task_id
  }, [item.conversation_id, currentConversation, pastConversations])

  const [$messageText, $pressable, $expanded, markdownRules, mediaType] = React.useMemo(() => {
    const isMyMessage = overrideText ? false : item.role === "member"

    const widthToUse = containerWidth && containerWidth > 0 ? containerWidth : width
    const bubbleWidthMultiplier = 0.85
    const maxBubbleWidth = Math.min(
      widthToUse * bubbleWidthMultiplier,
      Math.max(widthToUse - 20, 0),
    )

    const textColor = isMyMessage ? colors.userBubbleText : colors.sidekickBubbleText

    // Explicit lineHeight forces Fabric (New Architecture) to allocate the correct
    // vertical space for each text line when fontScale > 1. Without it, the layout
    // height is computed from the unscaled fontSize while text paints at the scaled
    // size, causing the bubble to be too short and clip the bottom of the text.
    const BASE_FONT_SIZE = 15
    const scaledLineHeight = Math.ceil(BASE_FONT_SIZE * fontScale * 1.35)

    // On web, a long unbreakable string (no spaces) won't wrap by default and
    // overflows the bubble's maxWidth. Force long words to break onto the next
    // line so text never extends past the bubble edge. (RN native already wraps.)
    const $wordBreakStyle =
      Platform.OS === "web"
        ? ({ wordBreak: "break-word", overflowWrap: "anywhere" } as unknown as TextStyle)
        : {}

    const $messageText = {
      body: {
        margin: 0,
        padding: 0,
        fontSize: BASE_FONT_SIZE,
        color: textColor,
        fontFamily: typography.fonts.figtree.normal,
        flexShrink: 1,
        ...$wordBreakStyle,
      },
      paragraph: {
        marginTop: 0,
        marginBottom: 10,
        color: textColor,
      },
      bullet_list: {
        marginVertical: 10,
        color: textColor,
      },
      ordered_list: {
        marginVertical: 10,
        color: textColor,
      },
      list_item: {
        color: textColor,
      },
      text: {
        color: textColor,
        lineHeight: scaledLineHeight,
        ...$wordBreakStyle,
      },
      blockquote: {
        backgroundColor: "transparent",
        borderLeftColor: colors.textDim,
        borderLeftWidth: 3,
        paddingLeft: 10,
        marginVertical: 10,
        marginLeft: 0,
        color: textColor,
      },
      // Ensure headings also use proper text color and scaled line height
      heading1: {
        color: textColor,
        lineHeight: Math.ceil(32 * fontScale * 1.2),
      },
      heading2: {
        color: textColor,
        lineHeight: Math.ceil(24 * fontScale * 1.2),
      },
      heading3: {
        color: textColor,
        lineHeight: Math.ceil(18 * fontScale * 1.2),
      },
      heading4: {
        color: textColor,
        lineHeight: scaledLineHeight,
      },
      heading5: {
        color: textColor,
        lineHeight: scaledLineHeight,
      },
      heading6: {
        color: textColor,
        lineHeight: scaledLineHeight,
      },
      // Code blocks
      code_inline: {
        color: textColor,
        backgroundColor: isMyMessage ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
        borderRadius: 3,
        paddingHorizontal: 4,
        paddingVertical: 2,
        lineHeight: scaledLineHeight,
      },
      code_block: {
        color: textColor,
        backgroundColor: isMyMessage ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
        borderRadius: 5,
        padding: 10,
        lineHeight: scaledLineHeight,
      },
      // Emphasis (italic)
      em: {
        color: textColor,
        fontStyle: "italic" as const,
        lineHeight: scaledLineHeight,
      },
    }

    let mediaType: MediaType | undefined
    const messageEvents = item.events
    if (messageEvents && messageEvents.length > 0) {
      mediaType = getMediaTypeFromEvent(messageEvents[0], apiHost)
    }

    const textContent = overrideText || item.text || ""
    const hasMultipleLines = textContent.includes("\n")
    const needsFullWidth = !isMyMessage && (hasMultipleLines || !!mediaType)

    const $pressable: ViewStyle = {
      alignSelf: "flex-start",
      maxWidth: maxBubbleWidth,
      ...themed($bubble),
      backgroundColor: colors.sidekickBubbleBackground,
      borderWidth: 0,
      ...(needsFullWidth && {
        width: maxBubbleWidth,
      }),
      ...(isMyMessage && {
        alignSelf: "flex-end",
        backgroundColor: colors.userBubbleBackground,
        borderRadius: 12,
        padding: 12,
        marginRight: 16,
      }),
    }

    const $expanded: ViewStyle = {
      alignSelf: "flex-start",
      ...themed([$bubble, $bubbleBelow]),
      marginHorizontal: spacing.md,
      ...(isMyMessage && {
        alignSelf: "flex-end",
      }),
    }

    // It's a bit unclear (buggy) in the Component debug tree how these nodes are actually rendered based on these rules,
    // but it's necessary to thread the node key into the output component for the rule or else you get list item key errors.
    // See the example implementation for details:
    // https://github.com/iamacup/react-native-markdown-display?tab=readme-ov-file#rules
    const markdownRules: RenderRules = {
      strong: (node) => (
        <Text
          key={node.key}
          style={
            {
              fontFamily: typography.fonts.figtree.bold,
              color: textColor,
            } as TextStyle
          }
          text={node.children[0].content}
        />
      ),
      link: (node, children) => {
        const url = node.attributes.href
        return (
          <Text
            key={node.key}
            style={{ color: colors.primary, textDecorationLine: "underline" }}
            onPress={() => {
              if (url.startsWith("mailto:")) {
                Linking.openURL(url)
              } else {
                handleWebsitePress(url)
              }
            }}
          >
            {children}
          </Text>
        )
      },
    }

    return [$messageText, $pressable, $expanded, markdownRules, mediaType]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    overrideText,
    item.role,
    item.text,
    item.active_esk_type,
    item.sidekick_name,
    item.events,
    themed,
    colors.white,
    colors.primary,
    typography.fonts.figtree.normal,
    typography.fonts.figtree.bold,
    width,
    apiHost,
    containerWidth,
    fontScale,
  ])

  // ... rest of the file ...
  // Wait, I need to make sure I don't break messageSenderName in MediaThumbnail

  // Add URL detection with support for www. URLs and common TLDs
  const urls = React.useMemo(() => {
    const text = overrideText || item.text
    if (!text) return []

    // Use linkify-it to find all URLs in the processed text.
    // Only keep fully qualified URLs (those with an explicit schema like https://)
    // to avoid treating bare domain references like "Monday.com" as clickable links.
    const matches = linkify.match(text) || []
    const allUrls = matches
      .filter((match) => match.schema === "https:" || match.schema === "http:")
      .map((match) => match.url)

    // Process and validate URLs
    const validUrls = allUrls
      .map((url) => {
        try {
          // If it's a mailto link, leave it as is
          if (url.startsWith("mailto:")) {
            const parsedUrl = new URL(url)
            return parsedUrl.href
          }

          // mobile browsers don't support http://, so we convert it to https://
          const securedUrl = url.replace("http://", "https://")
          // If it starts with www. or just a domain, prepend https://
          const urlWithProtocol = securedUrl.startsWith("https")
            ? securedUrl
            : `https://${securedUrl}`

          // URL constructor will validate and normalize the URL
          const parsedUrl = new URL(urlWithProtocol)
          return parsedUrl.href
        } catch (error) {
          // Log the invalid URL for debugging
          console.warn("[MessageBubble] Invalid URL:", { url, error })
          return null
        }
      })
      .filter(Boolean) // Remove any null values

    // Create a map to track unique domains
    const domainMap = new Map()

    // Process URLs to identify and deduplicate by domain
    validUrls.forEach((url) => {
      try {
        if (url.startsWith("mailto:")) {
          // For mailto: URLs, new URL().hostname is always "" (opaque path), so deduplicate
          // by the full email address instead.
          if (!domainMap.has(url)) {
            domainMap.set(url, url)
          }
          return
        }

        const parsedUrl = new URL(url)
        // normalize the domain by removing www if present
        const domain = parsedUrl.hostname.toLowerCase().replace(/^www\./, "")

        // If we already have this domain, skip
        if (domainMap.has(domain)) {
          return
        }

        // Store the URL with this domain
        domainMap.set(domain, url)
      } catch (error) {
        console.warn("[MessageBubble] Error processing URL for deduplication:", { url, error })
      }
    })

    // Convert the map values back to an array
    const uniqueUrls = [...domainMap.values()]

    return uniqueUrls
  }, [overrideText, item.text])

  // Add phone number detection using libphonenumber-js (Google's libphonenumber port).
  // findPhoneNumbersInText handles all separator variants (Unicode dashes, spaces, dots, etc.)
  // and returns structured results with the original matched text from the message.
  // Emergency/short-code numbers (911, 988, 211, etc.) are below libphonenumber's minimum digit
  // count, so they are detected separately with a word-boundary regex and merged in.
  const phoneNumbers = React.useMemo(() => {
    const text = overrideText || item.text
    if (!text) return []

    // Short US service numbers that libphonenumber-js won't parse (below its minimum digit count).
    // N11 codes are standardized 3-digit dialing shortcuts assigned by the FCC.
    const shortServiceNumbers = [
      "911", // Emergency services (police, fire, ambulance)
      "988", // Suicide & Crisis Lifeline (mental health crisis support)
      "211", // Community services referral (food, housing, mental health resources)
      "311", // Non-emergency municipal services (city/local government)
      "411", // Directory assistance
      "511", // Traffic and travel information
      "711", // Telecommunications relay for deaf/hard-of-hearing (TTY/TDD)
      "811", // Call before you dig (underground utility line locating)
    ]
    const shortNumberRegex = new RegExp(
      `(?<![0-9])(${shortServiceNumbers.join("|")})(?![0-9])`,
      "g",
    )
    const shortMatches = text.match(shortNumberRegex) ?? []

    const deviceRegion = (getLocales()[0]?.regionCode ?? "US") as CountryCode
    const found = findPhoneNumbersInText(text, deviceRegion)
    const fullMatches = found.map((match) =>
      match.number.country === deviceRegion
        ? match.number.formatNational()
        : match.number.formatInternational(),
    )

    return [...new Set([...shortMatches, ...fullMatches])]
  }, [overrideText, item.text])

  const handleWebsitePress = (url: string) => {
    openWebsite(url, router.push)
  }

  const [copied, setCopied] = React.useState(false)

  const handleCopy = async () => {
    const textToCopy = overrideText || item.text
    if (textToCopy) {
      // Extract URLs from media events only
      const mediaUrls: string[] = []
      if (item.events && item.events.length > 0) {
        item.events.forEach((event) => {
          const mediaType = getMediaTypeFromEvent(event, apiHost)
          if (mediaType && "url" in mediaType) {
            mediaUrls.push(mediaType.url)
          }
        })
      }

      // Include media URLs in the copied text if they exist
      let fullTextToCopy = textToCopy
      if (mediaUrls.length > 0) {
        const urlSection = mediaUrls.join("\n")
        fullTextToCopy = `${textToCopy}\n\n${urlSection}`
      }
      await Clipboard.setStringAsync(fullTextToCopy)
      if (!(Platform.OS === "web")) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // Align received messages to left side of the screen, with blue background.
  return item.role === "system" ? (
    <SystemMessage item={item} />
  ) : (
    <View>
      <Pressable
        style={[$pressable, style, expanded && { marginBottom: 0 }]}
        onPress={() => {
          LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
          // Only expand messages from the sidekick
          if (item.role !== "member") {
            onExpand?.(item)
          }
        }}
        onLongPress={() => {
          if (!(Platform.OS === "web")) {
            Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light)
          }
          onFeedbackPress?.(item)
        }}
      >
        {children || (
          <View>
            {!!overrideText && (
              <Markdown style={$messageText} rules={markdownRules}>
                {overrideText}
              </Markdown>
            )}
            {!!item.text && (
              <Markdown style={$messageText} rules={markdownRules}>
                {item.text}
              </Markdown>
            )}
            {!!mediaType && (
              <MediaThumbnail
                mediaType={mediaType}
                priority={item.created_at ? new Date(item.created_at).getTime() : 0}
                conversationTaskId={conversationTaskId}
              />
            )}
            {item.role === "esk" &&
              urls.map((url) => {
                if (url.startsWith("mailto:")) {
                  return (
                    <Pressable
                      key={`url-${url}`}
                      onPress={() => Linking.openURL(url as string)}
                      style={themed($linkPreviewContainer)}
                    >
                      <FontAwesome name="envelope-o" size={16} color={colors.primary} />
                      <Text style={[$linkText, { color: colors.primary }]}>{url.substring(7)}</Text>
                    </Pressable>
                  )
                } else {
                  return (
                    <Pressable key={`url-${url}`} onPress={() => handleWebsitePress(url as string)}>
                      <WebsiteThumbnail websiteUrl={url as string} />
                    </Pressable>
                  )
                }
              })}
            {/* Display clickable phone numbers */}
            {item.role === "esk" &&
              phoneNumbers.map((phone) => {
                const digitsOnly = phone.replace(/\D/g, "")
                return (
                  <View key={`phone-${phone}`} style={themed($phoneContainer)}>
                    <Text style={themed($phoneNumber)}>{phone}</Text>
                    <View style={$actionButtons}>
                      <TouchableOpacity
                        style={themed($actionButton)}
                        onPress={() => Linking.openURL(`tel:${digitsOnly}`)}
                      >
                        <FontAwesome name="phone" style={themed($actionButtonIcon)} />
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={themed($actionButton)}
                        onPress={() => Linking.openURL(`sms:${digitsOnly}`)}
                      >
                        <FontAwesome name="comment" style={themed($actionButtonIcon)} />
                      </TouchableOpacity>
                    </View>
                  </View>
                )
              })}
            {/* {debugMode && item?.isSwitchoverMessage && <ESKSwitchThumbnail item={item} />} */}
          </View>
        )}
      </Pressable>
      {expanded && !!item.created_at && (
        <View style={[$expanded, $expandedRow]}>
          <Text style={themed($timestampText)}>
            {timeAgo(item.created_at).minutes > 0 ? `Sent ${formatTimeAgo(item.created_at)}` : ""}
          </Text>
          <View style={$feedbackContainer}>
            <Pressable style={$iconButton} onPress={handleCopy}>
              <FontAwesome
                name={copied ? "check" : "clone"}
                size={20}
                color={copied ? colors.text : colors.textDim}
              />
            </Pressable>
            <Pressable
              style={$iconButton}
              onPress={() => onFeedbackPress?.(item, FeedbackRating.THUMBS_UP)}
            >
              <FontAwesome name="thumbs-o-up" size={20} color={colors.textDim} />
            </Pressable>
            <Pressable
              style={$iconButton}
              onPress={() => onFeedbackPress?.(item, FeedbackRating.THUMBS_DOWN)}
            >
              <FontAwesome name="thumbs-o-down" size={20} color={colors.textDim} />
            </Pressable>
          </View>
        </View>
      )}
    </View>
  )
})

// MessageBubble.whyDidYouRender = true

const $bubble: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  borderRadius: 10,
  borderWidth: StyleSheet.hairlineWidth * PixelRatio.get(),
  marginHorizontal: spacing.xs,
  marginVertical: 5,
  padding: 10,
  paddingBottom: 0, // To offset the markdown paragraph bottom margin
})

const $bubbleBelow: ThemedStyle<ViewStyle> = ({ colors }) => ({
  marginTop: 0,
  marginBottom: 0,
  padding: 0,
  backgroundColor: colors.transparent,
  borderWidth: 0,
})

const $timestampText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 12,
  fontFamily: typography.fonts.figtree.normal,
  color: colors.textDim,
})

const $feedbackContainer: ViewStyle = {
  flexDirection: "row",
  justifyContent: "flex-end",
  paddingHorizontal: 10,
  paddingVertical: 5,
  gap: 10,
}

const $iconButton: ViewStyle = {
  padding: 5,
}

const $expandedRow: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
}

const $phoneContainer: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 8,
  marginBottom: 10,
  backgroundColor: colors.transparent,
  borderRadius: 8,
})

const $phoneNumber: ThemedStyle<TextStyle> = ({ colors }) => ({
  flex: 1,
  color: colors.text,
})

const $actionButtons: ViewStyle = {
  flexDirection: "row",
  gap: 8,
}

const $actionButton: ThemedStyle<ViewStyle> = ({ colors }) => ({
  padding: 8,
  paddingHorizontal: 16,
  borderRadius: 6,
  backgroundColor: colors.buttonBackground,
  width: 48,
  alignItems: "center",
})

const $actionButtonIcon: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.buttonText,
  fontSize: 16,
})

const $linkPreviewContainer: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flexDirection: "row",
  alignItems: "center",
  padding: 8,
  marginBottom: 10,
  backgroundColor: colors.transparent,
  borderRadius: 8,
  gap: 8,
})

const $linkText: TextStyle = {
  textDecorationLine: "underline",
}
